import type { PaymentRequest } from '$api-clients/hooks';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_ABBREV = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const DEFAULT_DATA = MONTHS.map((month, index) => ({
  adjustedGrossCommission: null,
  salesValueGoal: null,
  salesVolume: null,
  month,
  periodMonth: index + 1,
}));

/** Format a period (year + month) for L365 chart axis, e.g. "Apr '24" */
export function formatPeriodMonthLabel(
  periodYear: number,
  periodMonth: number,
): string {
  const abbrev = MONTH_ABBREV[periodMonth - 1] ?? String(periodMonth);
  const shortYear = String(periodYear).slice(-2);
  return `${abbrev} '${shortYear}`;
}

export const accumulateValues = (
  data: Pick<
    PaymentRequest<'/reporting/summary'>['Data']['results'][number],
    'adjustedGrossCommission' | 'salesValueGoal' | 'salesVolume' | 'periodMonth'
  >[],
) => {
  let accumulatedAdjustedGrossCommission = 0;
  let accumulatedSalesValueGoal = 0;
  let accumulatedSalesVolume = 0;

  return data.map(
    ({ adjustedGrossCommission, salesValueGoal, salesVolume, periodMonth }) => {
      if (adjustedGrossCommission) {
        accumulatedAdjustedGrossCommission += adjustedGrossCommission;
      }

      if (salesValueGoal) {
        accumulatedSalesValueGoal += salesValueGoal;
      }

      if (salesVolume) {
        accumulatedSalesVolume += salesVolume;
      }

      return {
        adjustedGrossCommission: accumulatedAdjustedGrossCommission,
        salesValueGoal: accumulatedSalesValueGoal,
        salesVolume: accumulatedSalesVolume,
        periodMonth,
      };
    },
  );
};

const NUMBER_OF_MONTHS = 12;
export const extrapolateSalesValueGoals = (
  data: {
    periodMonth?: number;
    salesValueGoal: number;
  }[],
) => {
  if (data.length === 0) {
    return Array.from({ length: NUMBER_OF_MONTHS }, (_, indx) => ({
      salesValueGoal: null,
      periodMonth: indx + 1,
    }));
  }

  if (data.length >= NUMBER_OF_MONTHS) {
    return data.slice(0, NUMBER_OF_MONTHS);
  }

  if (data.length === 1) {
    const [first] = data;
    const firstGoal = first.salesValueGoal;
    const increment = firstGoal;
    const extrapolatedGoals = [
      { salesValueGoal: firstGoal, periodMonth: first.periodMonth },
    ];

    for (let index = 1; index < NUMBER_OF_MONTHS; index += 1) {
      extrapolatedGoals.push({
        salesValueGoal: extrapolatedGoals[index - 1].salesValueGoal + increment,
        periodMonth: index + 1,
      });
    }

    return extrapolatedGoals;
  }

  const extrapolatedGoals = [...data];
  const lastIndex = data.length - 1;
  const previousIndex = data.length - 2;
  const increment =
    data[lastIndex].salesValueGoal - data[previousIndex].salesValueGoal;

  for (let index = data.length; index < NUMBER_OF_MONTHS; index += 1) {
    extrapolatedGoals[index] = {
      salesValueGoal: extrapolatedGoals[index - 1].salesValueGoal + increment,
      periodMonth: index + 1,
    };
  }

  return extrapolatedGoals;
};
